import { Element } from "@xmpp/xml"
import {
  type Json,
  isInstant,
  isMinutes,
  isNullable,
  isObject,
  isText,
} from "@/lib/aiWidgetJson"

export const AI_WIDGET_TACHO_XMLNS = "urn:slickshift:ai-widget:tacho:1"

export type TachoCardActivity =
  | "driving"
  | "work"
  | "rest"
  | "availability"
  | "unknown"

export type TachoCardCounters = {
  breakMin: number | null
  todayMin: number | null
  weekMin: number | null
  twoWeeksMin: number | null
}

export type TachoCardStopReason =
  | "break"
  | "today"
  | "week"
  | "twoWeeks"
  | "none"

export type TachoCardCrewMember = {
  slot: 1 | 2
  name: string | null
  activity: TachoCardActivity
  sourceAt: string | null
  remainingMin: number | null
  counters: TachoCardCounters
}

export type TachoCardRow = {
  plate: string
  name: string | null
  activity: TachoCardActivity
  sourceAt: string | null
  moving: boolean
  stopAt: string | null
  stopReason: TachoCardStopReason
  swapAt: string | null
  remainingMin: number | null
  counters: TachoCardCounters
  crew: [TachoCardCrewMember, TachoCardCrewMember] | null
}

export type TachoCardWeeklyRest = {
  requiredMin: number | null
  dueAt: string | null
  endsAt: string | null
  inProgress: boolean
}

export type TachoDriverCardData = TachoCardRow & {
  rest: TachoCardWeeklyRest
  remainingDaysOfDriving: number | null
  remainingDailyExtensions: number | null
  reducedDailyRestsLeft: number | null
}

export type TachoCardLanguage = "pl" | "en"

type TachoCardText = { language: TachoCardLanguage; sentence: string }

export type TachoCard = TachoCardText &
  (
    | { version: 1; kind: "fleet"; rows: TachoCardRow[] }
    | { version: 1; kind: "driver"; driver: TachoDriverCardData }
  )

const ACTIVITIES = new Set([
  "driving",
  "work",
  "rest",
  "availability",
  "unknown",
])
const REASONS = new Set(["break", "today", "week", "twoWeeks", "none"])

function isCounters(v: unknown): v is TachoCardCounters {
  return (
    isObject(v) &&
    isNullable(v.breakMin, isMinutes) &&
    isNullable(v.todayMin, isMinutes) &&
    isNullable(v.weekMin, isMinutes) &&
    isNullable(v.twoWeeksMin, isMinutes)
  )
}

function isCrewMember(v: unknown, slot: 1 | 2): v is TachoCardCrewMember {
  return (
    isObject(v) &&
    v.slot === slot &&
    isNullable(v.name, isText) &&
    ACTIVITIES.has(v.activity as string) &&
    isNullable(v.sourceAt, isInstant) &&
    isNullable(v.remainingMin, isMinutes) &&
    isCounters(v.counters)
  )
}

function isRow(v: unknown): v is TachoCardRow {
  return (
    isObject(v) &&
    typeof v.plate === "string" &&
    isNullable(v.name, isText) &&
    ACTIVITIES.has(v.activity as string) &&
    isNullable(v.sourceAt, isInstant) &&
    typeof v.moving === "boolean" &&
    isNullable(v.stopAt, isInstant) &&
    REASONS.has(v.stopReason as string) &&
    isNullable(v.swapAt, isInstant) &&
    isNullable(v.remainingMin, isMinutes) &&
    isCounters(v.counters) &&
    (v.crew === null ||
      (Array.isArray(v.crew) &&
        v.crew.length === 2 &&
        isCrewMember(v.crew[0], 1) &&
        isCrewMember(v.crew[1], 2)))
  )
}

function isDriver(v: unknown): v is TachoDriverCardData {
  return (
    isRow(v) &&
    isObject((v as Json).rest) &&
    isNullable(((v as Json).rest as Json).requiredMin, isMinutes) &&
    isNullable(((v as Json).rest as Json).dueAt, isInstant) &&
    isNullable(((v as Json).rest as Json).endsAt, isInstant) &&
    typeof ((v as Json).rest as Json).inProgress === "boolean" &&
    isNullable((v as Json).remainingDaysOfDriving, isMinutes) &&
    isNullable((v as Json).remainingDailyExtensions, isMinutes) &&
    isNullable((v as Json).reducedDailyRestsLeft, isMinutes)
  )
}

export function parseTachoCard(json: string): TachoCard | undefined {
  let data: unknown
  try {
    data = JSON.parse(json)
  } catch {
    return undefined
  }
  if (!isObject(data) || data.version !== 1) return undefined
  if (data.language !== "pl" && data.language !== "en") return undefined
  if (typeof data.sentence !== "string" || !data.sentence.trim())
    return undefined
  const text = { language: data.language, sentence: data.sentence } as const
  if (
    data.kind === "fleet" &&
    Array.isArray(data.rows) &&
    data.rows.every(isRow)
  ) {
    return { ...text, version: 1, kind: "fleet", rows: data.rows }
  }
  if (data.kind === "driver" && isDriver(data.driver)) {
    return { ...text, version: 1, kind: "driver", driver: data.driver }
  }
  return undefined
}

export function parseAiWidgetTacho(stanza: Element): TachoCard | undefined {
  const el = stanza.getChild("ai-widget-tacho", AI_WIDGET_TACHO_XMLNS)
  return el ? parseTachoCard(el.getText()) : undefined
}
